/**
 * API Base URL — einheitlich für Dev und Prod.
 *
 * Dev:  Vite-Proxy leitet /api/* an localhost:3001 weiter
 * Prod: Express API auf api.ortoai.administer.cloud
 */

declare const __VITE_API_PROXY_TARGET__: string | undefined

const IS_DEV = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')

/** LocalStorage-Override für API-URL (gesetzt via Einstellungen-Modal) */
const _apiOverride = typeof window !== 'undefined'
  ? localStorage.getItem('orto_api_override')
  : null

/** Base-URL der Express API (Auth, CRUD, projekt-scoped Endpoints) */
export const API_URL = _apiOverride
  ? _apiOverride  // Manuell überschrieben via Einstellungen
  : IS_DEV
    ? ''  // Dev: Vite-Proxy leitet /api/* weiter
    : (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_API_URL
        ? (import.meta as any).env.VITE_API_URL.replace(/\/api$/, '')
        : 'https://api.ortoai.administer.cloud')

/** Base-URL für neue CRUD-Endpunkte (z.B. /api/v1/annotations) */
export const API_V1 = `${API_URL}/api/v1`

/** Base-URL für Legacy-Endpunkte (z.B. /api/annotations) — wird schrittweise abgelöst */
export const API_LEGACY = `${API_URL}/api`

/**
 * Legacy API_BASE — wird noch von script.js und Proxy-Calls verwendet.
 * Für ortofapi.administer.cloud Proxy-Calls in Prod.
 */
export const API_BASE = IS_DEV
  ? 'http://localhost:8080'
  : 'https://ortofapi.administer.cloud'

// Für script.js zugänglich machen (hat keinen Zugang zu import.meta.env)
if (typeof window !== 'undefined') {
  (window as any)._expressApiBase = API_URL
  ;(window as any)._apiFetch = apiFetch
  const proxyTarget = IS_DEV ? (typeof __VITE_API_PROXY_TARGET__ !== 'undefined' ? __VITE_API_PROXY_TARGET__ : '?') : '—'
  console.log(`[API Config] API_URL = "${API_URL || '(via proxy → ' + proxyTarget + ')'}", IS_DEV = ${IS_DEV}`)
}

// ─── Auth-aware Fetch & Project-scoped URLs ──────────────────────────────────

/** Token-Refresh — einmalig, nicht parallel */
let _refreshing: Promise<boolean> | null = null

async function _tryRefreshToken(): Promise<boolean> {
  if (_refreshing) return _refreshing
  _refreshing = (async () => {
    const refreshToken = localStorage.getItem('refreshToken')
    if (!refreshToken) return false
    try {
      const res = await fetch(`${API_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      })
      if (!res.ok) return false
      const json = await res.json()
      const data = json.data || json
      if (data.accessToken) {
        localStorage.setItem('accessToken', data.accessToken)
        if (data.refreshToken) localStorage.setItem('refreshToken', data.refreshToken)
        console.log('[API] Token erfolgreich erneuert')
        return true
      }
      return false
    } catch { return false }
    finally { _refreshing = null }
  })()
  return _refreshing
}

/** Auth-aware fetch wrapper — fügt Authorization-Header automatisch hinzu, refresht Token bei 401 */
export async function apiFetch(url: string, options?: RequestInit): Promise<Response> {
  const doFetch = (tkn: string | null) => {
    const headers: Record<string, string> = {
      ...(options?.headers as Record<string, string> || {}),
    }
    if (tkn) headers['Authorization'] = `Bearer ${tkn}`
    if (!headers['Content-Type'] && !(options?.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json'
    }
    const fullUrl = url.startsWith('/') ? `${API_URL}${url}` : url
    return fetch(fullUrl, { ...options, headers })
  }

  const fullUrl = url.startsWith('/') ? `${API_URL}${url}` : url
  const method = options?.method || 'GET'
  console.log(`[API ${method}] ${fullUrl}`)

  let res = await doFetch(localStorage.getItem('accessToken'))

  // Bei 401: Token refreshen und nochmal versuchen
  if (res.status === 401) {
    const refreshed = await _tryRefreshToken()
    if (refreshed) {
      res = await doFetch(localStorage.getItem('accessToken'))
    }
  }

  // Logging
  const status = res.status
  const clone = res.clone()
  clone.json().then(data => {
    console.log(`[API ${status}] ${fullUrl}`, data)
  }).catch(() => {
    clone.text().then(t => console.log(`[API ${status}] ${fullUrl}`, t.substring(0, 200)))
  })
  return res
}

/** Build project-scoped API URL */
export function projectUrl(path: string): string {
  const { currentProjectId } = (window as any).__authStore?.getState?.() || {}
  if (!currentProjectId) {
    console.warn('No project selected, using legacy API')
    return `${API_V1}${path}`
  }
  return `${API_URL}/api/projects/${currentProjectId}${path}`
}
